import {
	useLocalParticipant,
	useMaybeRoomContext,
} from "@livekit/components-react";
import { captureException } from "@sentry/react";
import { Track } from "livekit-client";
import {
	ArrowUp,
	ChevronUp,
	Hand,
	LogOut,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	PhoneOff,
	Video,
	VideoOff,
	X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverAnchor,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCameraPermission } from "@/hooks/use-camera-permission";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useScreenShare } from "@/hooks/use-screen-share";
import { useStageModerator } from "@/hooks/use-stage";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { ControlBarSettings } from "./control-bar-settings";
import { ScreenSourcePicker } from "./screen-source-picker";

type AudienceControlsProps = {
	compact: boolean;
	isModerator: boolean;
	hasRequest: boolean;
	onJoinStage: () => void;
	onCancelRequest: () => void;
	onRequestStage: () => void;
};

function AudienceControls({
	compact,
	isModerator,
	hasRequest,
	onJoinStage,
	onCancelRequest,
	onRequestStage,
}: AudienceControlsProps) {
	if (isModerator) {
		return (
			<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
				<Button
					className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 text-foreground transition-all hover:bg-accent-foreground/10"
					onClick={onJoinStage}
					variant="ghost"
				>
					<ArrowUp className="@md:size-5 @sm:size-4 size-3 text-foreground" />
					{!compact && (
						<span className="ml-1 @md:text-sm @sm:text-xs text-[10px]">
							Join Stage
						</span>
					)}
				</Button>
			</div>
		);
	}

	if (hasRequest) {
		return (
			<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
				<Button
					className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
					onClick={onCancelRequest}
					variant="ghost"
				>
					<X className="@md:size-5 @sm:size-4 size-3 text-foreground" />
					{!compact && (
						<span className="ml-1 @md:text-sm @sm:text-xs text-[10px]">
							Cancel Request
						</span>
					)}
				</Button>
			</div>
		);
	}

	return (
		<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
			<Button
				className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
				onClick={onRequestStage}
				variant="ghost"
			>
				<Hand className="@md:size-5 @sm:size-4 size-3 text-foreground" />
				{!compact && (
					<span className="ml-1 @md:text-sm @sm:text-xs text-[10px] text-foreground">
						Request to Speak
					</span>
				)}
			</Button>
		</div>
	);
}

type StageControlBarProps = {
	channelId: string;
	compact?: boolean;
};

export function StageControlBar({
	channelId,
	compact = false,
}: StageControlBarProps) {
	const room = useMaybeRoomContext();
	const { localParticipant } = useLocalParticipant();
	const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
	const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);

	const user = useStore(authStore, (state) => state.user);
	const stageStates = useStore(coreStore, (state) => state.stageStates);
	const leaveStage = useStore(coreStore, (state) => state.leaveStage);
	const joinStage = useStore(coreStore, (state) => state.joinStage);
	const requestStageAccess = useStore(
		coreStore,
		(state) => state.requestStageAccess
	);
	const cancelStageRequest = useStore(
		coreStore,
		(state) => state.cancelStageRequest
	);
	const isModerator = useStageModerator(channelId);
	const handleLeaveChannel = useStore(
		uiStore,
		(state) => state.handleLeaveChannel
	);

	const { requestPermission: requestMicPermission } = useMicrophonePermission();
	const { requestPermission: requestCameraPermission } = useCameraPermission();
	const {
		screenShareEnabled,
		toggleScreenShare,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
	} = useScreenShare({
		localParticipant,
		sentryContext: "stage_control_bar_screen_share",
	});

	const stageState = stageStates.get(channelId);
	const isOnStage = user?.uid ? stageState?.speakers.includes(user.uid) : false;
	const hasRequest = user?.uid
		? (stageState?.requests.some((r) => r.odId === user.uid) ?? false)
		: false;

	const micEnabled =
		localParticipant?.isMicrophoneEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Microphone)?.track
			?.isMuted === false;

	const cameraEnabled =
		localParticipant?.isCameraEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Camera)?.track
			?.isMuted === false;

	const toggleMic = async () => {
		if (!localParticipant) return;
		if (!isOnStage) {
			toast.error("You must be on stage to use your microphone");
			return;
		}
		try {
			if (!micEnabled) {
				const granted = await requestMicPermission();
				if (!granted) return;
			}
			await localParticipant.setMicrophoneEnabled(!micEnabled);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "stage_control_bar_mic" } });
			toast.error("Failed to toggle microphone");
		}
	};

	const toggleCamera = async () => {
		if (!localParticipant) return;
		try {
			if (!cameraEnabled) {
				const granted = await requestCameraPermission();
				if (!granted) return;
			}
			await localParticipant.setCameraEnabled(!cameraEnabled);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "stage_control_bar_camera" },
				});
			toast.error("Failed to toggle camera");
		}
	};

	const handleScreenShare = async () => {
		if (!isOnStage) {
			toast.error("You must be on stage to share your screen");
			return;
		}
		await toggleScreenShare();
	};

	const handleJoinStage = async () => {
		try {
			await joinStage(channelId);
			toast.success("You joined the stage!");
		} catch {
			toast.error("Failed to join stage");
		}
	};

	const handleRequestStage = async () => {
		try {
			await requestStageAccess(channelId);
			toast.info("Request sent! Waiting for moderator approval.");
		} catch {
			toast.error("Failed to request stage access");
		}
	};

	const handleCancelRequest = async () => {
		try {
			await cancelStageRequest(channelId);
			toast.info("Request cancelled");
		} catch {
			toast.error("Failed to cancel request");
		}
	};

	const handleLeaveStage = async () => {
		try {
			if (localParticipant) {
				await localParticipant.setMicrophoneEnabled(false);
				const screenTrack = localParticipant.getTrackPublication(
					Track.Source.ScreenShare
				);
				if (screenTrack?.track) {
					await localParticipant.unpublishTrack(screenTrack.track);
				}
			}
			await leaveStage(channelId);
			toast.info("You left the stage");
		} catch {
			toast.error("Failed to leave stage");
		}
	};

	const leaveCall = () => {
		handleLeaveChannel();
		toast.info("Left stage channel");
	};

	if (!room) return null;

	return (
		<>
			<ScreenSourcePicker
				onOpenChange={setScreenPickerOpen}
				onSelect={handleElectronScreenShare}
				open={screenPickerOpen}
				sources={screenSources}
			/>
			<div className="@container flex items-center justify-center gap-2 border-t bg-card/50 p-4 backdrop-blur-sm">
				{/* 1. Microphone - only when on stage */}
				{isOnStage && (
					<Popover onOpenChange={setAudioSettingsOpen} open={audioSettingsOpen}>
						<PopoverAnchor asChild>
							<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
								<Button
									className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
									onClick={toggleMic}
									variant="ghost"
								>
									{micEnabled ? (
										<Mic className="@md:size-5 @sm:size-4 size-3 text-foreground" />
									) : (
										<MicOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
									)}
									<span className="sr-only">
										{micEnabled ? "Mute microphone" : "Unmute microphone"}
									</span>
								</Button>
								{!compact && (
									<>
										<div className="h-6 w-px bg-border" />
										<PopoverTrigger asChild>
											<Button
												className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-2 @sm:px-1.5 px-1 transition-all hover:bg-accent-foreground/10"
												variant="ghost"
											>
												<ChevronUp
													className={`@md:size-4 @sm:size-3 size-2.5 text-foreground transition-transform duration-200 ${audioSettingsOpen ? "rotate-180" : ""}`}
												/>
												<span className="sr-only">Audio settings</span>
											</Button>
										</PopoverTrigger>
									</>
								)}
							</div>
						</PopoverAnchor>
						<ControlBarSettings
							onOpenChange={setAudioSettingsOpen}
							open={audioSettingsOpen}
							type="audio"
						/>
					</Popover>
				)}

				{/* 2. Camera - always visible */}
				<Popover onOpenChange={setVideoSettingsOpen} open={videoSettingsOpen}>
					<PopoverAnchor asChild>
						<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
							<Button
								className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
								onClick={toggleCamera}
								variant="ghost"
							>
								{cameraEnabled ? (
									<Video className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								) : (
									<VideoOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								)}
								<span className="sr-only">
									{cameraEnabled ? "Turn off camera" : "Turn on camera"}
								</span>
							</Button>
							{!compact && (
								<>
									<div className="h-6 w-px bg-border" />
									<PopoverTrigger asChild>
										<Button
											className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-2 @sm:px-1.5 px-1 transition-all hover:bg-accent-foreground/10"
											variant="ghost"
										>
											<ChevronUp
												className={`@md:size-4 @sm:size-3 size-2.5 text-foreground transition-transform duration-200 ${videoSettingsOpen ? "rotate-180" : ""}`}
											/>
											<span className="sr-only">Video settings</span>
										</Button>
									</PopoverTrigger>
								</>
							)}
						</div>
					</PopoverAnchor>
					<ControlBarSettings
						onOpenChange={setVideoSettingsOpen}
						open={videoSettingsOpen}
						type="video"
					/>
				</Popover>

				{/* 3. Screen share - only when on stage */}
				{isOnStage && (
					<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
						<Button
							className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
							onClick={handleScreenShare}
							variant="ghost"
						>
							{screenShareEnabled ? (
								<MonitorOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
							) : (
								<Monitor className="@md:size-5 @sm:size-4 size-3 text-foreground" />
							)}
							<span className="sr-only">
								{screenShareEnabled ? "Stop sharing" : "Share screen"}
							</span>
						</Button>
					</div>
				)}

				{/* 4. Request/Join/Leave stage button */}
				{isOnStage ? (
					<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
						<Button
							className="@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 text-foreground transition-all hover:bg-accent-foreground/10"
							onClick={handleLeaveStage}
							variant="ghost"
						>
							<LogOut className="@md:size-5 @sm:size-4 size-3" />
							{!compact && (
								<span className="ml-1 @md:text-sm @sm:text-xs text-[10px]">
									Leave Stage
								</span>
							)}
						</Button>
					</div>
				) : (
					<AudienceControls
						compact={compact}
						hasRequest={hasRequest}
						isModerator={isModerator}
						onCancelRequest={handleCancelRequest}
						onJoinStage={handleJoinStage}
						onRequestStage={handleRequestStage}
					/>
				)}

				{/* 5. Disconnect */}
				<Button
					className="@md:h-12 @sm:h-10 h-8 rounded-full bg-destructive @md:px-4 @sm:px-3 px-2 text-destructive-foreground hover:bg-destructive/90"
					onClick={leaveCall}
				>
					<PhoneOff className="@md:size-5 @sm:size-4 size-3" />
					<span className="sr-only">Leave call</span>
				</Button>
			</div>
		</>
	);
}
